import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Home, Building, CheckCircle2, ArrowRight, Users, Award, Target, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import LeadCaptureModal from '@/components/LeadCaptureModal';
import BackButton from '@/components/BackButton';

const Services = () => {
  const [activeService, setActiveService] = useState(0);
  const [counters, setCounters] = useState({ projects: 0, units: 0, sqft: 0, years: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalInterest, setModalInterest] = useState('');
  const [modalSource, setModalSource] = useState('general');
  const [modalFormLocation, setModalFormLocation] = useState('services_cta');

  const scrollToDetails = (index) => {
    setActiveService(index);
    // Scroll to the detailed section with offset
    setTimeout(() => {
      const detailSection = document.getElementById('service-details');
      if (detailSection) {
        const elementPosition = detailSection.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 100; // Scroll 100px above the element
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  useEffect(() => {
    const targets = { projects: 50, units: 267, sqft: 1000000, years: 10 };
    const duration = 2000;
    const steps = 60;
    const increment = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setCounters({
        projects: Math.floor(targets.projects * progress),
        units: Math.floor(targets.units * progress),
        sqft: Math.floor(targets.sqft * progress),
        years: Math.floor(targets.years * progress),
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounters(targets);
      }
    }, increment);

    return () => clearInterval(timer);
  }, []);

  const services = [
    {
      icon: TrendingUp,
      title: 'Investment',
      tagline: 'Strategic partnerships for high-yield returns',
      description: 'Access high-yield urban real estate projects in one of the most dynamic markets in the U.S. - Jersey City.',
      features: [
        { 
          title: 'Strong Track Record', 
          desc: 'GN Management has delivered dozens of successful mixed-use and multifamily developments across Jersey City and nearby high-growth markets. From boutique residences to skyline-defining towers, each project reflects consistent execution and value creation. Our proven track record positions us as a trusted developer with long-term performance.', 
          icon: Award 
        },
        { 
          title: 'Market Access', 
          desc: 'With over a decade of presence in Jersey City, GN has gained early entry into some of the city\'s most promising urban corridors. Our local expertise and foresight allow us to identify undervalued zones ahead of the curve. This first-mover advantage translates into stronger returns and long-term appreciation - a success model that can be replicated in other high-growth urban markets across the U.S.', 
          icon: Target 
        },
        { 
          title: 'Integrated Execution', 
          desc: 'GN Management operates across the full development cycle - from land acquisition to construction, leasing, and asset management. This vertically integrated model ensures tight control over cost, quality, and timelines. By minimizing third-party dependencies, we protect investor interests and maximize delivery confidence.', 
          icon: Shield 
        },
        { 
          title: 'Recurring Returns', 
          desc: 'Our stabilized rental properties generate consistent rental income while appreciating in long-term value. We structure our assets to deliver both cash flow and capital growth, ensuring balanced returns. As our portfolio scales, so does the opportunity for compounding investor value.', 
          icon: TrendingUp 
        },
        { 
          title: 'Transparency', 
          desc: 'We maintain a clear, open channel with our partners through detailed reporting, pro forma models, and construction updates. Investors gain visibility into project progress and financial performance at every stage. This culture of transparency builds lasting trust and empowers smarter investment decisions.', 
          icon: CheckCircle2 
        },
      ],
      image: 'https://customer-assets.emergentagent.com/job_0caec195-4afb-498c-8de6-c94e30150b2f/artifacts/t4mvvru1_2022.jpg',
      color: 'from-emerald-500 to-teal-600',
    },
    {
      icon: Home,
      title: 'Rental',
      tagline: 'Premium living spaces with exceptional service',
      description: 'Luxury and boutique rental properties with full in-house management - from construction to tenant services.',
      features: [
        { 
          title: '267+ Units', 
          desc: 'Spanning 18 prime properties, our portfolio includes standout addresses like Singh Tower, MetroVue at Journal Square, Nanak Niwas, 68 Oakland Avenue, and Giles Avenue. These developments showcase our ability to deliver diverse rental offerings - from high-rise luxury to boutique residences - across Jersey City\'s most sought-after neighborhoods.', 
          icon: Building 
        },
        { 
          title: 'Prime Locations', 
          desc: 'Our rental properties are strategically positioned in key zones including Journal Square, The Heights, Hoboken, Exchange Place, and West Side. By anchoring our presence in Jersey City\'s most vibrant districts, we offer unmatched access to transit, lifestyle, and convenience - with plans to expand into other major urban markets.', 
          icon: Target 
        },
        { 
          title: 'In-House Expertise', 
          desc: 'GN Management oversees every phase of the process, from groundbreaking to tenant care, with zero reliance on third parties. Our fully integrated model ensures consistent quality, operational efficiency, and a seamless experience, all driven by a team of experienced industry leaders.', 
          icon: Users 
        },
        { 
          title: 'Tenant-Centric', 
          desc: 'At GN, tenants come first. Our leasing and property management teams are proactive, responsive, and dedicated to enhancing every resident\'s living experience. From fast maintenance response to thoughtful service touchpoints, we create homes people are proud to live in.', 
          icon: CheckCircle2 
        },
        { 
          title: 'Reliable Team', 
          desc: 'Our on-ground staff is professionally trained, experienced, and committed to service excellence. With dedicated maintenance and operations teams across all properties, we ensure peace of mind for residents and dependable support for every request.', 
          icon: Shield 
        },
      ],
      image: 'https://images.unsplash.com/photo-1745429523617-0d837856ca35?crop=entropy&cs=srgb&fm=jpg&q=85',
      color: 'from-blue-500 to-cyan-600',
    },
    {
      icon: Building,
      title: 'Construction',
      tagline: 'Excellence in every detail, from ground to rooftop',
      description: 'Full-spectrum construction management from pre-construction planning through closeout with rigorous quality control.',
      features: [
        { 
          title: 'Project Execution', 
          desc: 'GN offers full-spectrum construction management, from pre-construction planning through closeout - ensuring consistency, accountability, and efficiency at every stage. By managing the entire process in-house or through tightly vetted partners, GN streamlines decision-making and avoids the common disconnects that plague many large-scale developments.', 
          icon: Building 
        },
        { 
          title: 'Value Engineering', 
          desc: 'Before breaking ground, GN\'s team conducts:\n• Site feasibility assessments\n• Cost-benefit evaluations\n• Design coordination and constructability reviews\n• Accurate budgeting & value engineering\n\nThis careful groundwork ensures the construction process launches with financial and structural clarity, setting the tone for success.', 
          icon: Target 
        },
        { 
          title: 'Vetted Network', 
          desc: 'GN doesn\'t just hire contractors; they cultivate long-term relationships with high-performing trades and specialty subs who meet rigorous standards in:\n• Craftsmanship\n• Safety\n• Code compliance\n• On-time delivery\n\nEach subcontractor is evaluated not just on price, but on proven track record and alignment with GN\'s quality expectations.', 
          icon: Users 
        },
        { 
          title: 'Quality Control', 
          desc: 'Project superintendents and site managers maintain constant on-site oversight to:\n• Monitor work against timelines and specs\n• Address site challenges proactively\n• Maintain safe working conditions\n• Ensure code and regulatory compliance\n\nWeekly site reviews, digital documentation, and open communication with developers, architects, and engineers keep all stakeholders aligned.', 
          icon: Shield 
        },
        { 
          title: 'Progress Tracking', 
          desc: 'GN leverages construction management tools and reporting systems to give clients full visibility into:\n• Budget performance\n• Change orders\n• Work in progress\n• Inspections and compliance milestones\n\nTransparency is central to GN\'s construction philosophy. Clients are never in the dark about what\'s happening, when, and why.', 
          icon: CheckCircle2 
        },
      ],
      image: 'https://images.unsplash.com/photo-1740904257914-45adcd97040a?crop=entropy&cs=srgb&fm=jpg&q=85',
      color: 'from-orange-500 to-amber-600',
    },
  ];

  return (
    <div className="services-page">
      {/* Hero Section with Animated Stats */}
      <section className="relative py-32 pt-40 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1542811029-dd22535dfad1?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        </div>
        
        {/* Back Button */}
        <div className="absolute top-24 left-6 z-10">
          <BackButton />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 z-10">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 animate-in">Our Services</h1>
          <p className="text-lg sm:text-xl max-w-3xl text-gray-300 animate-in" style={{ animationDelay: '0.2s' }}>
            Comprehensive real estate solutions spanning investment, rental, and construction management.
          </p>
        </div>
      </section>

      {/* Interactive Service Cards */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-black">What We Offer</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Three core services that drive value for our partners and communities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  data-testid={`service-card-${index}`}
                  onClick={() => scrollToDetails(index)}
                  className={`group relative bg-white rounded-3xl p-8 cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden ${
                    activeService === index ? 'shadow-2xl ring-2 ring-[#20B2AA] ring-offset-2 bg-gradient-to-br from-teal-50/30 to-white' : 'shadow-lg'
                  }`}
                >
                  <div className={`absolute top-0 left-0 right-0 rounded-t-3xl bg-[#20B2AA] transition-all duration-300 ${
                    activeService === index ? 'h-1.5' : 'h-1'
                  }`} />
                  
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-4 rounded-2xl bg-gray-100 transition-all duration-300 group-hover:bg-gray-200">
                      <Icon className="h-8 w-8 text-[#20B2AA]" />
                    </div>
                    <h3 className="text-2xl font-bold text-black">{service.title}</h3>
                  </div>
                  
                  <p className="text-sm text-[#20B2AA] font-medium mb-4">{service.tagline}</p>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  
                  <div className="flex items-center gap-2 text-[#20B2AA] font-medium text-sm group-hover:gap-4 transition-all duration-200">
                    Explore Details
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detailed Service View */}
          <div id="service-details" className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="relative h-[400px] lg:h-auto">
                <img
                  src={services[activeService].image}
                  alt={services[activeService].title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              
              <div className="p-8 lg:p-12">
                <div className="flex items-center gap-3 mb-6">
                  {React.createElement(services[activeService].icon, { className: "h-10 w-10 text-[#20B2AA]" })}
                  <h3 className="text-3xl font-bold text-black">{services[activeService].title}</h3>
                </div>
                
                <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                  {services[activeService].description}
                </p>

                <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
                  {services[activeService].features.map((feature, fIndex) => {
                    const FeatureIcon = feature.icon;
                    return (
                      <AccordionItem key={fIndex} value={`item-${fIndex}`} className="border-b border-gray-200">
                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex items-center gap-3 text-left">
                            <div className="w-10 h-10 rounded-xl bg-[#20B2AA]/10 flex items-center justify-center flex-shrink-0">
                              <FeatureIcon className="h-5 w-5 text-[#20B2AA]" />
                            </div>
                            <div>
                              <div className="font-semibold text-black">{feature.title}</div>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pl-13 text-gray-600">
                          <div className="whitespace-pre-line leading-relaxed">
                            {feature.desc}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>

                <div className="mt-8">
                  <Button
                    size="lg"
                    onClick={() => {
                      // Set source based on active service
                      const serviceSources = ['investment', 'rental', 'construction', 'general'];
                      const serviceLocations = ['investment_service', 'rental_service', 'construction_service', 'development_service'];
                      setModalTitle(`Request Information - ${services[activeService].title}`);
                      setModalInterest(activeService === 0 ? 'Investor' : activeService === 1 ? 'Tenant' : 'Other');
                      setModalSource(serviceSources[activeService] || 'general');
                      setModalFormLocation(serviceLocations[activeService] || 'services');
                      setIsModalOpen(true);
                    }}
                    className="bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-full px-8 py-6 hover:-translate-y-1 transition-all duration-200"
                  >
                    Request Information
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose GN Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-black">Why Choose GN Management</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Proven expertise, integrated solutions, and unwavering commitment to excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Award,
                title: 'Proven Track Record',
                description: 'Over a decade of successful projects and satisfied partners across Jersey City.',
                stat: '50+ Projects',
              },
              {
                icon: Users,
                title: 'Full-Service Approach',
                description: 'From acquisition to management, we handle every aspect in-house for seamless execution.',
                stat: '30+ Team Members',
              },
              {
                icon: Target,
                title: 'Market Intelligence',
                description: 'Deep local knowledge and data-driven insights give our partners a competitive edge.',
                stat: '10+ Years Local',
              },
              {
                icon: Shield,
                title: 'Transparent Partnership',
                description: 'Regular updates, clear communication, and complete visibility throughout the process.',
                stat: '96% Occupancy',
              },
            ].map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className="flex items-start gap-6">
                    <div className="p-4 rounded-2xl bg-gradient-to-br from-[#20B2AA] to-[#189a93] transition-transform duration-300 group-hover:scale-110 flex-shrink-0">
                      <ItemIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-bold text-black">{item.title}</h3>
                        <span className="text-sm font-bold text-[#20B2AA] bg-[#20B2AA]/10 px-3 py-1 rounded-full">
                          {item.stat}
                        </span>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Snapshot - Enhanced */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1542811029-dd22535dfad1?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">Investment Snapshot: Jersey City</h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Why Jersey City is one of the most dynamic real estate markets in the U.S.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { label: 'Population Growth', value: '+17%', icon: TrendingUp },
              { label: 'Transit to Manhattan', value: '20 min', icon: Target },
              { label: 'Rental Occupancy', value: '~96%', icon: Home },
              { label: 'GN Asset Portfolio', value: '1M+ sq ft', icon: Building },
              { label: 'Development Pipeline', value: 'Highest in NJ', icon: Award },
            ].map((stat, index) => {
              const StatIcon = stat.icon;
              return (
                <div
                  key={index}
                  className="group bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 text-center hover:bg-white/10 transition-all duration-300 hover:-translate-y-2 scale-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <StatIcon className="h-8 w-8 text-[#20B2AA] mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
                  <div className="text-3xl font-bold text-[#20B2AA] mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-300">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-[3rem] p-12 lg:p-16 text-white text-center shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0">
              <div className="absolute inset-0" style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_0caec195-4afb-498c-8de6-c94e30150b2f/artifacts/hdwhxr02_2022.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <div className="absolute inset-0 bg-black/60" />
            </div>
            <div className="relative">
              <h2 className="text-4xl sm:text-5xl font-bold mb-6">Ready to Partner with Us?</h2>
              <p className="text-xl mb-10 max-w-2xl mx-auto text-white font-medium">
                Whether you're an investor, prospective tenant, or seeking construction services, let's discuss how we can work together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  data-testid="contact-services-btn"
                  size="lg"
                  onClick={() => {
                    setModalTitle('Get in Touch');
                    setModalInterest('');
                    setModalSource('general');
                    setModalFormLocation('services_cta');
                    setIsModalOpen(true);
                  }}
                  className="bg-white hover:bg-gray-100 text-[#20B2AA] px-10 py-6 text-lg rounded-full hover:-translate-y-1 transition-all duration-200 shadow-lg"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Link to="/portfolio">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg rounded-full transition-all duration-200"
                  >
                    View Our Work
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        defaultInterest={modalInterest}
        source={modalSource}
        formLocation={modalFormLocation}
      />
    </div>
  );
};

export default Services;
